import { useMutation } from "@apollo/client"
import cn from "classnames"
import React, { useCallback } from "react"
import { useTranslation } from "react-i18next"
import { graphql } from "@/api"
import LoadingAnimation from "@/common/components/loading"
import useMedia from "@/common/hooks/use_media"
import tailwindTheme from "@/common/styles/theme.module.css"
import { toggleScroll, basketAddToGA4 } from "@/common/util"
import { setBasketStateDoc } from "@/menu/components/basket_modal"
import { CartFill } from "@/skylticons"

const basketAddDoc = graphql(`
  mutation OrderItemBasketAdd($orderItemId: Int!) {
    orderItemBasketAdd(orderItemId: $orderItemId) {
      success
      landingUrl

      item {
        id
        priceSek
        totalCount
        sizeDisplay
        hangType
        materialNameForGtm
        exampleId
        signTypeKey
        discountPercentage
      }

      status {
        ...BasketModal_Order
      }
    }
  }
`)

export interface AddOrderItemToCartProps {
  orderItemId: number
  classes?: string
  copyBtn?: boolean
}

const AddOrderItemToCart: React.FC<AddOrderItemToCartProps> = ({ orderItemId, classes, copyBtn }) => {
  const [setBasketState] = useMutation(setBasketStateDoc)
  const desktop = useMedia(`(min-width: ${tailwindTheme.screensXl})`)
  const { t } = useTranslation()

  const openBasket = useCallback(() => {
    setBasketState({ variables: { open: true, addedNotice: true } })

    if (!desktop) {
      toggleScroll(true)
    }
  }, [setBasketState, desktop])

  const [addMutation, addMutationResult] = useMutation(basketAddDoc, {
    update: (store, { data: orderItemBasketAddData }) => {
      if (orderItemBasketAddData?.orderItemBasketAdd?.success) {
        const orderItem = orderItemBasketAddData.orderItemBasketAdd.item
        const id = orderItem.exampleId?.toString() || null

        basketAddToGA4(
          id,
          orderItem.priceSek,
          orderItem.totalCount,
          orderItem.discountPercentage,
          orderItem.signTypeKey,
          orderItem.sizeDisplay,
          orderItem.hangType,
          orderItem.materialNameForGtm,
          orderItemBasketAddData.orderItemBasketAdd.landingUrl
        )
        openBasket()
      }
    },
  })

  const addBasket = useCallback(() => {
    addMutation({ variables: { orderItemId: orderItemId } })
  }, [addMutation, orderItemId])

  const loading = addMutationResult.loading

  return (
    <button
      id="add_to_cart"
      className={cn("order-item-to-cart button", classes, {
        "button-emphasis": !copyBtn,
        "button-secondary": copyBtn,
      })}
      type="button"
      onClick={addBasket}
      disabled={loading}
    >
      {loading ? (
        <LoadingAnimation />
      ) : (
        <span className="flex items-center justify-center">
          {!copyBtn && <CartFill />}
          {copyBtn ? t("order.copy_to_cart") : t("signomatic.add_to_basket")}
        </span>
      )}
    </button>
  )
}

export default AddOrderItemToCart
